<template>
  <v-container>
    <v-card class="pa-4">
      <v-layout
        row
        justify-space-between
      >
        <v-flex xs3>
          <v-avatar
            size="75px"
            tile>
            <img
              src="@/assets/logo.png"
              alt="Baxter State Park"
            >
          </v-avatar>
        </v-flex>
        <v-flex
          class="text-xs-center"
          xs8
        >
          <h1>Invoice</h1>
        </v-flex>
        <v-flex
          class="text-xs-right"
          xs3
        >
          Baxter State Park<br>
          64 Balsam Drive<br>
          Millinocket, ME 04462<br>
          (207) 723-5140<br>
        </v-flex>
      </v-layout>
      <p class="text-xs-center">
        Warning: This is not your reservation. To view reservations go to the <router-link :to="{ path: '/reservations' }">Reservation Viewer</router-link>
      </p>
      <v-layout
        row
        justify-space-between
        class="mb-4"
      >
        <v-flex>
          {{ person.lastName }}, {{ person.firstName }}<br>
          {{ person.address1 }}<br>
          <template v-if="person.address2">
            {{ person.address2 }}<br>
          </template>
          {{ person.city }}, {{ person.state }} {{ person.zip }}<br>
          {{ person.country }}<br>
        </v-flex>
        <v-flex>
          Invoice ID: {{ invoice.invoiceID }}<br>
          Camper ID: {{ person.personID }}<br>
          Created: {{ new Date(invoice.stamp).toLocaleDateString() }}<br>
          Printed: {{ new Date().toLocaleDateString() }}<br>
          Admin: {{ invoice.adminID || 'None' }}<br>
          <span v-if="invoice.commercialPermitID"> CUPS #: {{ invoice.commercialPermitID }} </span>
        </v-flex>
        <v-flex
          xs4
          class="font-weight-bold"
        >
          This invoice will reflect any future changes,
          including cancellations and incurred fees.
          It should not be taken as a finalized statement.
        </v-flex>
      </v-layout>
    </v-card>
    <v-divider/>
    <v-card class="pa-4 text-capitalize">
      <!-- start headers -->
      <v-layout
        row
        class="mb-3"
      >
        <v-flex xs10>
          <h2>Item</h2>
        </v-flex>
        <v-flex
          xs2
          class="text-xs-right"
        >
          <h2>Amount</h2>
        </v-flex>
      </v-layout>
      <!-- start reservations -->
      <v-layout
        v-for="item in reservations"
        :key="item.ID"
        row
      >
        <v-flex xs4>
          {{ item.item }}
        </v-flex>
        <v-flex
          xs6
          class="grey--text"
        >
          {{ item.details }}
        </v-flex>
        <v-flex
          xs2
          class="text-xs-right"
        >
          <v-layout>
            <v-flex
              xs5
              class="text-xs-right"
            >
              <strike v-if="invoice.discount != 0">${{ (item.price*100/invoice.discount).toFixed(2) }}</strike>
            </v-flex>
            <v-flex
              xs7
              class="text-xs-right"
            >
              ${{ item.price }}
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
      <!-- start deleted items -->
      <v-layout
        v-for="item in invoiceDeletedItems"
        :key="item.invoiceDeletedItemID"
        row
        class="red--text"
      >
        <v-flex xs10>
          {{ item.description }}: Cancelled on {{ new Date(item.timestamp).toLocaleDateString() }} by {{ item.actorID }}
        </v-flex>
        <v-flex
          xs2
          class="text-xs-right"
        >
          <v-layout>
            <v-flex
              xs5
              class="text-xs-right"
            >
              <strike v-if="invoice.discount != 0">${{ (item.price*100/invoice.discount).toFixed(2) }}</strike>
            </v-flex>
            <v-flex
              xs7
              class="text-xs-right"
            >
              ${{ item.price }}
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
      <!-- end deleted items-->
      <template v-if="otherCharges.length">
        <h4 class="my-3">Other Charges</h4>
        <v-layout
          v-for="item in otherCharges"
          :key="item.ID"
          row
        >
          <v-flex xs4>
            {{ item.item }}
          </v-flex>
          <v-flex
            xs6
            class="grey--text"
          >
            {{ item.details }}
          </v-flex>
          <v-flex
            xs2
            class="text-xs-right"
          >
            ${{ item.price }}
          </v-flex>
        </v-layout>
      </template>
      <!-- start payments -->
      <h4 class="my-3">Payments/Refunds</h4>
      <v-layout
        v-for="item in payments"
        :key="item.paymentID"
        row
      >
        <v-flex xs10>
          {{ item.paymentTypeID }}: {{ item.paymentID }}-{{ item.confirmation }}
        </v-flex>
        <v-flex
          xs2
          class="text-xs-right"
        >
          ${{ item.amount }}
        </v-flex>
      </v-layout>
      <!-- end payments-->
      <!-- start refunds -->
      <v-layout
        v-for="item in baxterBuckEntries"
        :key="item.accountEntryID"
        row
      >
        <v-flex xs10>
          Refund: {{ item.accountID }} - {{ item.accountEntryID }}
        </v-flex>
        <v-flex
          xs2
          class="text-xs-right"
        >
          <v-layout>
            <v-flex
              xs5
              class="text-xs-right red--text"
            >
              <span v-if="item.debit > 0">${{ item.debit }}</span>
            </v-flex>
            <v-flex
              xs7
              class="text-xs-right green--text"
            >
              <span v-if="item.credit > 0">- ${{ item.credit }}</span>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-card>
    <!-- end refunds -->
    <v-divider/>
    <!-- start balances -->
    <v-card class="pa-4">
      <v-layout row>
        <v-flex xs7>
          <v-layout row>
            <h4>Reservation Discount Applied</h4>
            <span class="ml-5">{{ invoice.discount }}%</span>
          </v-layout>
        </v-flex>
        <v-flex xs5>
          <v-layout row>
            <v-flex xs8>
              <h4>Reservations Total</h4>
            </v-flex>
            <v-flex
              xs4
              class="text-xs-right"
            >
              ${{ reservationsTotal.toFixed(2) }}
            </v-flex>
          </v-layout>
          <v-layout row>
            <v-flex xs8>
              <h4>Other Charges</h4>
            </v-flex>
            <v-flex
              xs4
              class="text-xs-right"
            >
              ${{ otherChargesTotal.toFixed(2) }}
            </v-flex>
          </v-layout>
          <v-layout row>
            <v-flex xs8>
              <h4>Payments</h4>
            </v-flex>
            <v-flex
              xs4
              class="text-xs-right"
            >
              ${{ paymentTotal.toFixed(2) }}
            </v-flex>
          </v-layout>
          <v-layout
            v-if="deletedTotal"
            row
          >
            <v-flex xs6>
              <h4>Cancellations</h4>
            </v-flex>
            <v-flex
              xs3
              class="text-xs-right red--text"
            >
              ${{ deletedTotal.toFixed(2) }}
            </v-flex>
            <v-flex xs3/>
          </v-layout>
          <v-layout
            v-if="refundTotal"
            row
          >
            <v-flex xs8>
              <h4>Refunds</h4>
            </v-flex>
            <v-flex
              xs4
              class="text-xs-right green--text"
            >
              -${{ refundTotal.toFixed(2) }}
            </v-flex>
          </v-layout>
          <v-layout row>
            <v-flex xs8>
              <h4>Balance Due</h4>
            </v-flex>
            <v-flex
              xs4
              class="text-xs-right"
            >
              ${{ balance.toFixed(2) }}
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-card>
    <v-card class="elevation-4 pa-4">
      <v-layout row>
        <v-flex>
          <h3>Notes</h3>
        </v-flex>
        <v-flex>
          {{ invoice.note }}
        </v-flex>
      </v-layout>
    </v-card>
    <!-- end balance -->
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
export default {
  data () {
    return {
      invoiceID: this.$route.params.id
    }
  },
  computed: {
    ...mapGetters({
      invoice: 'invoices/invoice',
      invoiceLines: 'invoices/invoiceLines',
      invoiceDeletedItems: 'invoices/invoiceDeletedItems',
      payments: 'invoices/payments',
      invoiceAccountEntries: 'invoices/invoiceAccountEntries',
      person: 'person/person',
      searchResults: 'person/searchResults'
    }),
    reservations () {
      return this.invoiceLines.filter((item) => item.type !== 'otherCharge')
    },
    otherCharges () {
      return this.invoiceLines.filter((item) => item.type === 'otherCharge')
    },
    baxterBuckEntries () {
      return this.invoiceAccountEntries.filter((entry) => (entry.accountID === 'baxterBuck' || entry.accountID === 'refund') && (entry.accountEntryTypeID === 'cancellation' || entry.accountEntryTypeID === 'refund'))
    },
    deletedTotal () {
      return this.invoiceDeletedItems.reduce((total, item) => (total + Number(item.price)), 0)
    },
    reservationsTotal () {
      return this.reservations.reduce((total, item) => (total + Number(item.price)), 0) + this.deletedTotal
    },
    otherChargesTotal () {
      return this.otherCharges.reduce((total, item) => (total + Number(item.price)), 0)
    },
    paymentTotal () {
      return this.payments.reduce((total, payment) => total + Number(payment.amount), 0)
    },
    balance () {
      return (
        this.reservationsTotal +
        this.otherChargesTotal -
        this.paymentTotal -
        this.deletedTotal +
        this.refundTotal
      )
    },
    refundTotal () {
      return this.baxterBuckEntries.reduce((total, entry) => total + Number(entry.credit) - Number(entry.debit), 0)
    }
  },
  mounted () {
    let invoice = {
      invoiceID: this.invoiceID
    }
    this.getInvoice(invoice)
    this.getInvoiceLines(invoice)
    this.getInvoiceDeletedItems(invoice)
    this.getInvoicePayments(invoice)
    this.getInvoiceAccountEntries(invoice)
  },
  methods: {
    ...mapActions({
      getInvoice: 'invoices/getInvoice',
      getInvoiceLines: 'invoices/getInvoiceLines',
      getInvoiceDeletedItems: 'invoices/getInvoiceDeletedItems',
      getInvoicePayments: 'invoices/getInvoicePayments',
      getInvoiceAccountEntries: 'invoices/getInvoiceAccountEntries',
      searchPeople: 'person/searchPeople',
      setProxyPerson: 'person/setProxyPerson'
    })
  }
}
</script>
